import { useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Button, Form, NavLink } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import Select, { components } from 'react-select';
import {
  useCreateGroupMutation,
  useLeaveToGroupMutation,
  useMoveToGroupMutation
} from '@/app/KeywordTracker/api/keywordsApi';
import { Formik } from 'formik';
import classNames from 'classnames';
import { object, string } from 'yup';
import { errorMessage, successMessage } from '@/utils/toast';
import FalconCloseButton from '@/components/common/FalconCloseButton';
import { useSelector } from 'react-redux';
import { useGetGroupsForSelectQuery } from '@/app/KeywordTracker/api/groupsApi';
import Flex from '@/components/common/Flex';
import * as yup from 'yup';
import useDomain from '@/hooks/useDomain';
import { updateTransaction } from '@/app/common/components/ag-grid/updateTransaction';
import type { GridDrawerProps } from './types';
import type { OptionProps, NoticeProps } from 'react-select';
import type { SelectOption } from './types';

const MoveToGroupForSelectedRows = ({ show, setShow, gridRef }: GridDrawerProps) => {
  const handleClose = () => setShow(false);
  return (
    <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false}>
      <Modal.Header>
        <Modal.Title>
          <div className="d-flex flex-wrap gap-3 align-items-center">
            <div className="m-0">مدیریت دسته</div>
          </div>
        </Modal.Title>
        <FalconCloseButton onClick={handleClose} />
      </Modal.Header>
      <MoveToGroupBody handleClose={handleClose} gridRef={gridRef} />
    </Modal>
  );
};

export default MoveToGroupForSelectedRows;

interface MoveToGroupBodyProps {
  handleClose: () => void;
  gridRef: GridDrawerProps['gridRef'];
}

const MoveToGroupBody = ({ handleClose, gridRef }: MoveToGroupBodyProps) => {
  const { domain_slug } = useDomain();

  const [menuOpen, setMenuOpen] = useState(false);
  const selectedKeys = useSelector(state => state.selectedKeys);
  const [moveToGroup] = useMoveToGroupMutation();
  const [leaveToGroup] = useLeaveToGroupMutation();
  const { data: groups, isFetching: isFetchingGroups } =
    useGetGroupsForSelectQuery(domain_slug);
  return (
    <Modal.Body className="pb-0 pt-0">
      <Formik
        onSubmit={async ({ group_id, isMove }, action) => {
          isMove
            ? moveToGroup({
                keyword_ids: selectedKeys.map(key => key.id),
                group_id: group_id?.value,
                domain_slug
              })
                .unwrap()
                .then(res => {
                  updateTransaction(res.data, gridRef);
                  successMessage('دسته با موفقیت به کلمات اضافه شد');
                })
                .catch(err => {
                  errorMessage('خطایی در افزودن دسته وجود داره');
                  console.error(err);
                })
            : leaveToGroup({
                keyword_ids: selectedKeys.map(key => key.id),
                group_id: group_id?.value,
                domain_slug
              })
                .unwrap()
                .then(res => {
                  updateTransaction(res.data, gridRef);
                  successMessage('دسته با موفقیت از کلمات حذف شد.');
                })
                .catch(err => {
                  errorMessage('خطایی در حذف دسته وجود داره');
                  console.error(err);
                });
          handleClose();
        }}
        validationSchema={yup.object().shape({
          isMove: yup.boolean().required('عملیات رو مشخص کنید.'),
          group_id: yup.object().required('دسته رو انتخاب کنید.')
        })}
        initialValues={{ isMove: null, group_id: null, inputValue: '' }}
      >
        {({
          values,
          errors,
          touched,
          setFieldError,
          handleSubmit,
          setFieldValue
        }) => (
          <Form
            onSubmit={e => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <Form.Group className="my-3">
              <Form.Label>کلمات انتخاب شده ({selectedKeys.length})</Form.Label>
              <Form.Control
                // readOnly
                disabled
                rows={selectedKeys.length > 3 ? 3 : selectedKeys.length}
                as="textarea"
                value={selectedKeys.map(({ label }) => `${label}`).join('\n')}
              />
            </Form.Group>
            <Form.Group className="my-3">
              <Form.Label>انتخاب دسته</Form.Label>

              {!isFetchingGroups && groups?.data && (
                <Select
                  classNamePrefix="react-select"
                  menuShouldScrollIntoView={true}
                  closeMenuOnSelect={true}
                  menuIsOpen={menuOpen}
                  onMenuOpen={() => setMenuOpen(true)}
                  onInputChange={(newValue, { action }) => {
                    if (action !== 'input-blur' && action !== 'menu-close') {
                      setFieldValue('inputValue', newValue); // Only update input if not closing
                    } else {
                      setTimeout(() => setMenuOpen(false), 150);
                    }
                  }}
                  inputValue={values.inputValue}
                  placeholder="انتخاب کن"
                  styles={{
                    menu: (baseStyles, state) => ({
                      position: 'absolute',
                      // overflow: 'auto',
                      zIndex: 9999,
                      width: '100%'
                    }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      direction: 'rtl !important',
                      paddingLeft: '10px',
                      paddingRight: '10px',
                      borderColor:
                        errors.group_id &&
                        touched.group_id &&
                        'var(--falcon-form-invalid-border-color) !important',
                      color: 'var(--falcon-emphasise) !important',
                      boxShadow: 'none !important',
                      borderRadius: '5px 5px 5px 5px !important',
                      width: '100% !important',
                      cursor: 'pointer'
                    }),
                    input: (baseStyles, state) => ({
                      ...baseStyles,
                      direction: 'ltr !important',
                      color: 'var(--falcon-emphasise) !important',
                      boxShadow: 'none !important'
                    })
                  }}
                  components={{
                    Option: OptionLayout,
                    NoOptionsMessage: props => (
                      <NoOptionsMessage
                        {...props}
                        setFieldValue={setFieldValue}
                        setFieldError={setFieldError}
                      />
                    )
                  }}
                  onChange={e => {
                    setFieldValue('group_id', e);
                  }}
                  isMulti={false}
                  value={values?.group_id}
                  isSearchable={true}
                  noOptionsMessage={() => 'یافت نشد'}
                  options={groups?.data}
                />
              )}
              <Form.Control.Feedback
                className="d-block"
                type={
                  errors.group_id || errors.inputValue ? 'invalid' : 'valid'
                }
              >
                {(touched.group_id || errors.inputValue) &&
                  (errors.group_id || errors.inputValue)}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Label>عملیات</Form.Label>
            <Flex className="gap-3 flex-wrap">
              <Form.Check
                isInvalid={errors.isMove}
                type="radio"
                className="form-check radio-select"
              >
                <Form.Check.Input
                  isInvalid={errors.isMove}
                  onChange={({ target }) => {
                    target.checked && setFieldValue('isMove', true);
                  }}
                  checked={values.isMove}
                  type="radio"
                  id="add"
                />
                <Form.Check.Label htmlFor="add">
                  اضافه به دسته{' '}
                </Form.Check.Label>
              </Form.Check>{' '}
              <Form.Check type="radio" className="form-check radio-select">
                <Form.Check.Input
                  isInvalid={errors.isMove}
                  onChange={({ target }) => {
                    target.checked && setFieldValue('isMove', false);
                  }}
                  checked={values.isMove === false}
                  type="radio"
                  id="remove"
                />
                <Form.Check.Label htmlFor="remove">
                  خروج از دسته
                </Form.Check.Label>
              </Form.Check>
            </Flex>
            <Form.Control.Feedback
              className="d-block mt-n2 mb-2"
              type={errors.isMove ? 'invalid' : 'valid'}
            >
              {touched.isMove && errors.isMove}
            </Form.Control.Feedback>
            {/* دکمه های آخر صفحه */}
            <Modal.Footer>
              <Button
                className="d-flex gap-2 align-items-center"
                variant="outline-secondary"
                onClick={handleClose}
              >
                <FontAwesomeIcon size="lg" icon="times" />
                لغو
              </Button>
              <Button
                className="d-flex gap-2 align-items-center border-0"
                // onClick={() => handleSubmit()}
                type="submit"
                variant="falcon-primary"
              >
                اعمال کن
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal.Body>
  );
};

const OptionLayout = (props: OptionProps<SelectOption>) => {
  const { innerProps, innerRef } = props;
  return (
    <>
      <style>{`
          .customized-searchbox-option:hover{
            background-color:var(--falcon-gray-link-hover-300) !important;
          }
          .customized-searchbox-option{
            background-color:var(--falcon-tertiary-bg) !important;
          }
          `}</style>
      <div
        ref={innerRef}
        {...innerProps}
        style={{
          backgroundColor: props.isSelected
            ? 'var(--falcon-gray-link-hover-100)'
            : 'var(--falcon-dropdown-bg-global)'
        }}
        className={classNames(
          'p-2 cursor-pointer border border-1 customized-searchbox-option',
          {}
        )}
      >
        <div className="fs-0 text-900">{props.data.label}</div>
      </div>
    </>
  );
};

interface NoOptionsMessageProps extends NoticeProps<SelectOption, false> {
  setFieldValue: (field: string, value: unknown) => void;
  setFieldError: (field: string, message: string | undefined) => void;
}

const NoOptionsMessage = (props: NoOptionsMessageProps) => {
  const { data: userInfo, isLoading, isError } = useGetUserInfoQuery();
  const { domain_slug, domain } = useDomain();
  const [createGroup] = useCreateGroupMutation();
  const inputValue = props.selectProps.inputValue;
  const setInptut = value => props.setFieldValue('inputValue', value);
  return (
    <components.NoOptionsMessage {...props}>
      <Formik
        onSubmit={async ({ title }, action) => {
          createGroup({
            title: title.trim(),
            keyword_ids: [],
            domain_slug
          })
            .unwrap()
            .then(res => {
              if (res?.success) {
                successMessage('دسته با موفقیت اضافه شد');
                setInptut('');
              } else if (res?.resultCode === 50004)
                errorMessage('دسته ای با این عنوان وجود داره');
            })
            .catch(err => {
              errorMessage('خطایی در ایجاد تگ وجود داره');
              console.error(err);
            });
          // setShow(false);
        }}
        initialValues={{ title: inputValue, keyword_ids: [] }}
        enableReinitialize
        validationSchema={object().shape({
          // keyword_ids: array().required('وارد کردن'),
          title: string()
            .trim()
            .required('لطفا نام دسته رو بنویسید')
            .max(50, 'نام دسته نباید بیشتر از ۵۰ کاراکتر باشد.')
        })}
      >
        {({ handleSubmit, errors }) => {
          if (errors.title) props.setFieldError('inputValue', errors.title);
          return (
            !isLoading && (
              <div
                className="d-flex  fs--1 align-items-center gap-2 justify-content-center"
                onMouseDown={e => e.stopPropagation()} // Prevent react-select focus
                onClick={e => e.stopPropagation()} // Prevent react-select focus
              >
                <div>یافت نشد!</div>
                <NavLink
                  className="d-flex border-bottom bordrer-1 navLinkForceColor active nav-link gap-1 justify-content-center align-items-center"
                  onClick={e => {
                    e.stopPropagation();
                    handleSubmit();
                  }}
                >
                  اضافه کن
                </NavLink>
              </div>
            )
          );
        }}
      </Formik>
    </components.NoOptionsMessage>
  );
};
